import { useState } from 'react'

import type {
  IntegrationsSyncRunOriginEnum,
  IntegrationsSyncRunOutputFragment,
  MarketplaceIntegrationWithIdsFragment,
} from 'generated/resources/graphql'
import { SyncRunOutputsDocument, SyncRunStatusChangeDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { useSubscription } from 'modules/core/hooks/useSubscription'

import { type ContextParameters } from 'modules/integrations/hooks/SyncContext'
import { useUpdateLastSyncRunQueryCache } from 'modules/integrations/hooks/SyncContext/useLastSyncRun'
import { getSyncRunStatusIdSessionStorage } from 'modules/integrations/lib/syncRun'

interface UseSyncRunStatusChangedSubscriptionProps {
  marketplaceIntegration: MarketplaceIntegrationWithIdsFragment | null
  syncEnabled: boolean
  contextParameters: ContextParameters
  origin: IntegrationsSyncRunOriginEnum
}

export const useSyncRunStatusChangedSubscription = ({
  marketplaceIntegration,
  syncEnabled,
  contextParameters,
  origin,
}: UseSyncRunStatusChangedSubscriptionProps) => {
  const [fetchSyncRunOutputs] = useLazyQuery(SyncRunOutputsDocument)
  const updateLastSyncRunQueryCache = useUpdateLastSyncRunQueryCache()
  const [syncRunOutputsLoading, setSyncRunOutputsLoading] = useState(false)

  useSubscription(SyncRunStatusChangeDocument, {
    onData: ({ data: subscriptionData }) => {
      const syncRun = subscriptionData.data?.integrationsSyncRunStatusChange?.syncRun
      if (!syncRun || !marketplaceIntegration?.id) return

      // TODO: API&Marketplace will improve this to support multiple parameters and agnostic to context.
      // We'll try to decouple this part using something like an "CapabilityObject". This object will expose
      // an interface with filterKeys and a set of params to apply filters similar to those lines, but context-agnostic.
      const contextPayrollRunId = contextParameters.payroll_run_id
      const syncRunPayrollRunId = syncRun.parameters?.find((p) => p.key === 'payroll_run_id')?.value
      if (contextPayrollRunId && contextPayrollRunId !== syncRunPayrollRunId) {
        return
      }

      updateLastSyncRunQueryCache({
        marketplaceIntegration,
        contextParameters,
        syncRun,
        origin,
      })
      // TODO: improve this by only fetching the fileUrl if mode is File
      const fileDownloadAvailable = getSyncRunStatusIdSessionStorage(syncRun.id.toString())
      if (!fileDownloadAvailable || !syncRun.finishedAt) return

      setSyncRunOutputsLoading(true)
        fetchSyncRunOutputs({ variables: { syncRunIds: [syncRun.id] } })
          .then((result) => {
            const file = result.data?.integrations?.syncRunOutputsConnection?.nodes?.[0]
            file?.fileUrl && downloadFile(file)
          })
          .finally(() => setSyncRunOutputsLoading(false))
    },
    skip: !syncEnabled,
  })
  return { syncRunOutputsLoading }
}

const downloadFile = (file: IntegrationsSyncRunOutputFragment) => {
  const link = document.createElement('a')
  link.href = file.fileUrl
  link.download = file.fileName
  link.click()
}
